import React, { useState, useContext, useMemo, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { FieldConfigurationContext } from "../../../../lib/context/FieldConfigurationContext";
import FieldConfigurationAPI from "../../../../lib/api/FieldConfiguration";
import { formatPhoneNumber } from "../../../../lib/PhoneNumber";
import DetailHeader from "../components/DetailHeader";
import AuditHistory from "../components/AuditHistory";
import UserForm from "./UserForm";
import "./UserDetail.css";

export default function UserDetail({ user, onBack, onUpdate }) {
  const history = useHistory();
  const { roles, companies, users } = useContext(FieldConfigurationContext);
  const [isEditing, setIsEditing] = useState(false);
  const [auditRefreshKey, setAuditRefreshKey] = useState(0);

  const configureUsersData = companies?.configureUsersData || {};
  const isMultiCompany = Object.keys(configureUsersData).length > 1;

  // Build per-company card data for the right panel
  const perCompanyCards = useMemo(() => {
    return Object.entries(configureUsersData)
      .map(([uuid, name]) => {
        const companyIdx = user.companyUuids?.indexOf(uuid) ?? -1;
        const isAssigned = companyIdx >= 0;

        // In multi-company mode, skip companies the user isn't assigned to
        if (!isAssigned && isMultiCompany) return null;

        const companyUserUuid = isAssigned
          ? user.uuids?.[companyIdx]
          : user.uuids?.[0] || user.uuid;

        const record = companyUserUuid
          ? users.data.find((u) => u.uuid === companyUserUuid)
          : null;

        const roleUuid = record?.roleUuid || record?.role;
        const role = roles.data.find((r) => r.uuid === roleUuid);
        const code = companies.codesMap?.[uuid];

        return {
          uuid,
          companyUserUuid,
          displayName: code ? `${name} (${code})` : name,
          roleUuid,
          roleName: role?.name || "—",
          isActive: record?.active !== false,
          isPending: record?.verified === false,
          notInvited: record?.verified === false && !record?.sent_at,
          employee: record?.employee || record?.code || "",
          groups: record?.groups || [],
          roleExpenses: role?.expenses === true,
          payExpensesViaAch: record?.pay_expenses_via_ach || false,
          achUpdatedAt: record?.ach_updated_at || null,
        };
      })
      .filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companies.configureUsersData,
    companies.codesMap,
    user,
    users.data,
    roles.data,
  ]);

  // Expense cards are fetched from api_expense separately from `users`, keyed
  // by company_user_uuid — { [companyUserUuid]: [{ name, gl_account_code }] }
  const [cardsByCompanyUser, setCardsByCompanyUser] = useState({});

  useEffect(() => {
    let cancelled = false;
    for (const { uuid: companyUuid, companyUserUuid } of perCompanyCards) {
      if (!companyUserUuid || cardsByCompanyUser[companyUserUuid]) continue;
      FieldConfigurationAPI.getExpenseCards(companyUserUuid, companyUuid).then(
        (result) => {
          if (cancelled) return;
          setCardsByCompanyUser((prev) => ({
            ...prev,
            [companyUserUuid]: result.data || [],
          }));
        }
      );
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perCompanyCards]);

  // If editing, show the UserForm component
  if (isEditing) {
    return (
      <UserForm
        user={user}
        onSave={(updatedUser) => {
          setIsEditing(false);
          onUpdate(updatedUser);
          setAuditRefreshKey((k) => k + 1);
        }}
        onCancel={() => setIsEditing(false)}
      />
    );
  }

  return (
    <div className="user-detail">
      <DetailHeader
        title={`${user.firstName} ${user.lastName}`}
        onBack={onBack}
        onEdit={() => setIsEditing(true)}
        isEditing={false}
        canEdit={true}
        showBack={true}
        backLabel="Back to Users"
        editLabel="Edit User"
      />

      <div className="user-detail__two-col">
        {/* Left Panel */}
        <div className="user-detail__left">
          <div className="user-detail__section user-detail__section--info">
            <h3 className="user-detail__section-title">Personal Information</h3>
            <div className="user-detail__fields">
              <div className="user-detail__name-row">
                <div className="user-detail__field">
                  <label className="user-detail__label">First Name</label>
                  <div className="user-detail__value">
                    {user.firstName || "—"}
                  </div>
                </div>
                <div className="user-detail__field">
                  <label className="user-detail__label">Last Name</label>
                  <div className="user-detail__value">
                    {user.lastName || "—"}
                  </div>
                </div>
              </div>

              <div className="user-detail__field">
                <label className="user-detail__label">Email</label>
                <div className="user-detail__value">{user.email || "—"}</div>
              </div>

              {user.mobileNumber && (
                <div className="user-detail__field">
                  <label className="user-detail__label">Mobile Number</label>
                  <div className="user-detail__value">
                    {formatPhoneNumber(user.mobileNumber)}
                  </div>
                </div>
              )}

              {(user.address || user.city || user.state || user.zip) && (
                <div className="user-detail__field">
                  <label className="user-detail__label">Address</label>
                  <div className="user-detail__value">
                    {user.address && <div>{user.address}</div>}
                    <div>
                      {[
                        user.city,
                        [user.state, user.zip].filter(Boolean).join(" "),
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="user-detail__section">
            <h3 className="user-detail__section-title">Last Login</h3>
            <div className="user-detail__value">
              {user.lastLogin
                ? new Date(user.lastLogin).toLocaleDateString()
                : "Never"}
            </div>
          </div>

          {isMultiCompany && (
            <div className="user-detail__section">
              <h3 className="user-detail__section-title">Companies</h3>
              <div className="user-detail__company-badges">
                {Object.entries(configureUsersData).map(([uuid, name]) => {
                  const companyIdx = user.companyUuids?.indexOf(uuid) ?? -1;
                  const isAssigned = companyIdx >= 0;
                  const companyUserUuid = isAssigned
                    ? user.uuids?.[companyIdx]
                    : null;
                  const companyUserRecord = companyUserUuid
                    ? users.data.find((u) => u.uuid === companyUserUuid)
                    : null;
                  const isActive = companyUserRecord?.active !== false;
                  const isPending = companyUserRecord?.verified === false;
                  const badgeClass = isAssigned
                    ? isPending
                      ? " user-detail__company-badge--assigned-pending"
                      : isActive
                      ? " user-detail__company-badge--assigned"
                      : " user-detail__company-badge--assigned-inactive"
                    : "";
                  const code = companies.codesMap?.[uuid];
                  return (
                    <span
                      key={uuid}
                      className={`user-detail__company-badge${badgeClass}`}
                    >
                      {code != null ? `${name} (${code})` : name}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Right Panel */}
        <div className="user-detail__right">
          <h3 className="user-detail__panel-title">
            Per-Company Configuration
          </h3>
          {perCompanyCards.length === 0 ? (
            <div className="user-detail__empty-state">
              No company assignments available.
            </div>
          ) : (
            perCompanyCards.map(
              ({
                uuid,
                companyUserUuid,
                displayName,
                roleUuid,
                roleName,
                isActive,
                isPending,
                notInvited,
                employee,
                groups,
                roleExpenses,
                payExpensesViaAch,
                achUpdatedAt,
              }) => (
                <div key={uuid} className="user-detail__company-card">
                  <div className="user-detail__company-card-header">
                    <span className="user-detail__company-card-name">
                      {displayName}
                    </span>
                    <span
                      className={`user-status-badge ${
                        isPending
                          ? "user-status-badge--pending"
                          : isActive
                          ? "user-status-badge--active"
                          : "user-status-badge--inactive"
                      }`}
                    >
                      {isPending
                        ? notInvited
                          ? "Not Invited"
                          : "Not Verified"
                        : isActive
                        ? "Active"
                        : "Inactive"}
                    </span>
                  </div>
                  <div className="user-detail__company-card-body">
                    {employee && (
                      <div className="user-detail__field">
                        <label className="user-detail__label">
                          Employee ID
                        </label>
                        <div className="user-detail__value">{employee}</div>
                      </div>
                    )}
                    <div className="user-detail__field">
                      <label className="user-detail__label">Role</label>
                      <div className="user-detail__value">
                        {roleName}
                        {roleUuid && (
                          <button
                            type="button"
                            className="user-detail__link-btn"
                            onClick={() =>
                              history.push(
                                `/admin/field-configuration/roles/${roleUuid}`
                              )
                            }
                          >
                            View
                          </button>
                        )}
                      </div>
                    </div>
                    {groups.length > 0 && (
                      <div className="user-detail__field">
                        <label className="user-detail__label">
                          Access Groups
                        </label>
                        <div className="user-detail__groups">
                          {groups.map((g, i) => (
                            <button
                              type="button"
                              key={g.uuid || i}
                              className="user-detail__group-tag"
                              disabled={!g.uuid}
                              onClick={() =>
                                g.uuid &&
                                history.push(
                                  `/admin/field-configuration/access_groups/${g.uuid}`
                                )
                              }
                            >
                              {g.name || g.uuid}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {(cardsByCompanyUser[companyUserUuid] || []).length > 0 && (
                      <div className="user-detail__field">
                        <label className="user-detail__label">Cards</label>
                        <div className="user-detail__cards">
                          {cardsByCompanyUser[companyUserUuid].map((card) => (
                            <div
                              key={card.uuid}
                              className="user-detail__card-row"
                            >
                              <span className="user-detail__card-description">
                                {card.name}
                              </span>
                              <span className="user-detail__card-gl-account">
                                {card.gl_account_code || "—"}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {companies?.manageAchData?.[uuid] && roleExpenses && (
                      <div className="user-detail__field">
                        <label className="user-detail__label">
                          Pay Expenses via ACH
                        </label>
                        <div className="user-detail__value">
                          <span
                            className={`user-status-badge ${
                              payExpensesViaAch
                                ? "user-status-badge--active"
                                : "user-status-badge--inactive"
                            }`}
                          >
                            {payExpensesViaAch ? "Yes" : "No"}
                          </span>
                          {achUpdatedAt && (
                            <span className="user-detail__ach-updated">
                              Updated{" "}
                              {new Date(achUpdatedAt).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            )
          )}
        </div>
      </div>

      <div className="user-detail__audit-section">
        <AuditHistory
          resource="company_users"
          uuid={user.uuid}
          refreshKey={auditRefreshKey}
          createdAt={user.created_at}
          fieldLabels={{
            code: "Employee ID",
            pay_expenses_via_ach: "Pay Expenses via ACH",
          }}
        />
      </div>
    </div>
  );
}
